import { defineStore } from 'pinia';
import { ModelType } from '~~/app/base/schemas/BaseSchema';
import EndpointFactory from '~~/app/factories/EndpointFactory';
import type Professional from '~~/app/marketplace/models/Professional';

/**
 * Holds the marketplace professional profiles linked to the current tenant and the
 * currently-edited (active) profile. Backs the Visibility section profile switcher (spec §4.4).
 */
export const useVisibilityProfileStore = defineStore('visibilityProfile', () => {
    const profiles = ref<Professional[]>([]);
    const activeProfileId = ref<string | null>(null);
    const loading = ref(false);
    const loaded = ref(false);

    const activeProfile = computed<Professional | null>(() => profiles.value.find((profile) => profile.getId() === activeProfileId.value) ?? null);

    const hasMultipleProfiles = computed<boolean>(() => profiles.value.length > 1);

    // Active profile is persisted client-side only, scoped per tenant (spec §4.1 — no server state).
    const storageKey = (): string => {
        const { tenant } = useAuthStore();
        return `visibility:active-profile:${tenant?.getId() ?? 'unknown'}`;
    };

    const setActiveProfile = (id: string): void => {
        if (!profiles.value.some((profile) => profile.getId() === id)) return;
        activeProfileId.value = id;
        useLocalStorage<string>(storageKey()).setValue(id);
    };

    // Default = most recently active profile. The API exposes no "last active" field yet (Q3),
    // so we fall back to the client-side persisted choice, then to the first returned profile.
    const resolveDefaultProfileId = (): string | null => {
        const persisted = useLocalStorage<string>(storageKey()).getValue();
        if (persisted && profiles.value.some((profile) => profile.getId() === persisted)) {
            return persisted;
        }
        return profiles.value[0]?.getId() ?? null;
    };

    let idsRequest: Promise<string[]> | null = null;

    const loadIds = (force = false): Promise<string[]> => {
        if (!idsRequest || force) {
            idsRequest = EndpointFactory.make(ModelType.PROFESSIONALS)
                .index()
                .then((response) => (response.data?.items ?? []).map((summary) => summary.getId()))
                .catch((error) => {
                    idsRequest = null;
                    throw error;
                });
        }
        return idsRequest;
    };

    const load = async (force = false): Promise<void> => {
        if (loaded.value && !force) return;
        loading.value = true;
        try {
            const endpoint = EndpointFactory.make(ModelType.PROFESSIONALS);
            // The list endpoint returns bare ids with empty attributes; the full profile
            // (name, media, …) only comes from the per-id detail call. Profile counts per
            // tenant are small, so fetching each detail is acceptable.
            const ids = await loadIds(force);
            const details = await Promise.all(ids.map((id) => endpoint.retrieve(id)));
            profiles.value = details.filter((detail) => detail.data).map((detail) => detail.data as Professional);
            activeProfileId.value = resolveDefaultProfileId();
            loaded.value = true;
        } finally {
            loading.value = false;
        }
    };

    // Keep the cached profile in sync after an edit. The My Page form modal save and every portfolio
    // mutation broadcast this event (the endpoint re-fetches + emits), so the active-profile-backed
    // cards, albums, photos and highlights refresh without an imperative refetch.
    useListen('marketplace:professional:updated', (professional: Professional) => {
        const index = profiles.value.findIndex((profile) => profile.getId() === professional.getId());
        if (index !== -1) profiles.value[index] = professional;
    });

    return {
        profiles,
        activeProfile,
        activeProfileId,
        hasMultipleProfiles,
        loading,
        loaded,
        load,
        loadIds,
        setActiveProfile,
    };
});
